import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import API from '../api/axios';
import { Icon } from '../components/SvgIcons';
import { getTenantUrl } from '../utils/tenant';

const MailIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2"></rect>
    <path d="m22 7-10 6L2 7"></path>
  </svg>
);

const PhoneIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const SuperAdminTenants = () => {
  const [tenants, setTenants] = useState([]);
  const [showCreateTenantModal, setShowCreateTenantModal] = useState(false);
  const [newTenant, setNewTenant] = useState({
    slug: '',
    university_name: '',
    tenant_type: '',
    contact_email: '',
    phone_number: '',
    description: '',
    logo: null
  });
  const [submittingTenant, setSubmittingTenant] = useState(false);
  const [fetchingTenants, setFetchingTenants] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalContent, setErrorModalContent] = useState({ title: '', message: '' });
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    fetchTenants();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!newTenant.university_name.trim()) {
      errors.university_name = 'Tenant name is required';
    }
    if (!newTenant.slug.trim()) {
      errors.slug = 'Tenant slug is required';
    } else if (!/^[a-z0-9-]+$/.test(newTenant.slug)) {
      errors.slug = 'Slug must only contain lowercase letters, numbers, and hyphens';
    }
    if (!newTenant.tenant_type) {
      errors.tenant_type = 'Please select a tenant type';
    }
    if (newTenant.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newTenant.contact_email)) {
      errors.contact_email = 'Please enter a valid email address';
    }
    if (newTenant.phone_number && !/^[\d\s+\-()]{7,20}$/.test(newTenant.phone_number)) {
      errors.phone_number = 'Please enter a valid phone number';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchTenants = async () => {
    setFetchingTenants(true);
    try {
      const { data } = await API.get('/superadmin/tenants');
      if (data.success) {
        setTenants(data.tenants || []);
      }
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
    } finally {
      setFetchingTenants(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    if (!validateForm()) return;
    setSubmittingTenant(true);
    try {
      const formData = new FormData();
      formData.append('slug', newTenant.slug);
      formData.append('university_name', newTenant.university_name);
      formData.append('tenant_type', newTenant.tenant_type);
      formData.append('contact_email', newTenant.contact_email);
      formData.append('phone_number', newTenant.phone_number);
      formData.append('description', newTenant.description);
      if (newTenant.logo) {
        formData.append('logo', newTenant.logo);
      }

      const { data } = await API.post('/superadmin/tenants', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (data.success) {
        setShowErrorModal(true);
        setErrorModalContent({
          title: 'Success',
          message: `Tenant '${data.tenant.university_name}' provisioned and live successfully!`
        });
        setShowCreateTenantModal(false);
        setFieldErrors({});
        setNewTenant({
          slug: '',
          university_name: '',
          tenant_type: 'university',
          contact_email: '',
          phone_number: '',
          description: '',
          logo: null
        });
        fetchTenants();
      }
    } catch (err) {
      setShowErrorModal(true);
      setErrorModalContent({
        title: 'Error',
        message: err.response?.data?.message || 'Failed to create tenant.'
      });
    } finally {
      setSubmittingTenant(false);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Tenant Management</h1>
        <p className="dashboard-subtitle" style={{ color: 'white' }}>Provision dedicated isolated database environments for universities or colleges</p>
      </div>

      <div className="card">
        <div style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Tenants ({tenants.length})</h3>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowCreateTenantModal(true)}
          >
            + Create Tenant
          </button>
        </div>
        <div style={{ padding: '1rem' }}>
          {fetchingTenants ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading tenants...</div>
          ) : tenants.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><Icon name="Building" size={40} /></div>
              <p>No tenants registered yet. Click "+ Create Tenant" to provision one.</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Slug / Subdomain</th>
                  <th>University / College Name</th>
                  <th>Contact Info</th>
                  <th>Database Name</th>
                  <th>Classification</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(tenant => (
                  <tr key={tenant.id}>
                    <td style={{ verticalAlign: 'middle' }}>
                      {tenant.logo_url ? (
                        <img
                          src={process.env.REACT_APP_DEV_API_URL ? `${process.env.REACT_APP_DEV_API_URL}${tenant.logo_url}` : `http://localhost:5000${tenant.logo_url}`}
                          alt={tenant.university_name}
                          style={{ height: '30px', maxWidth: '80px', objectFit: 'contain' }}
                        />
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>No Logo</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                        <code style={{ alignSelf: 'flex-start', backgroundColor: '#f1f5f9', padding: '0.2rem 0.4rem', borderRadius: '4px', color: '#0f172a', fontWeight: 'bold' }}>
                          {tenant.slug}
                        </code>
                        <a
                          href={getTenantUrl(tenant.slug)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: '0.75rem',
                            color: '#6366f1',
                            textDecoration: 'none',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                        >
                          {getTenantUrl(tenant.slug).replace('http://', '').replace('https://', '')} ↗
                        </a>
                      </div>
                    </td>
                    <td>
                      <strong>{tenant.university_name}</strong>
                      {tenant.description && (
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', maxWidth: '250px', whiteSpace: 'normal', wordBreak: 'break-word', fontStyle: 'italic', lineHeight: '1.4' }}>
                          {tenant.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem', color: '#475569' }}>
                        {tenant.contact_email && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                            <MailIcon /> <a href={`mailto:${tenant.contact_email}`} style={{ color: '#4f46e5', textDecoration: 'none' }}>{tenant.contact_email}</a>
                          </span>
                        )}
                        {tenant.phone_number && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                            <PhoneIcon /> <span style={{ fontWeight: '500' }}>{tenant.phone_number}</span>
                          </span>
                        )}
                        {!tenant.contact_email && !tenant.phone_number && (
                          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>N/A</span>
                        )}
                      </div>
                    </td>
                    <td><code style={{ fontSize: '0.85rem' }}>{tenant.db_name}</code></td>
                    <td>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        textTransform: 'capitalize',
                        backgroundColor: tenant.tenant_type === 'university' ? '#dcfce7' : tenant.tenant_type === 'college' ? '#fee2e2' : '#fef3c7',
                        color: tenant.tenant_type === 'university' ? '#15803d' : tenant.tenant_type === 'college' ? '#b91c1c' : '#92400e'
                      }}>
                        {tenant.tenant_type || 'university'}
                      </span>
                    </td>
                    <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Tenant Modal */}
      {showCreateTenantModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2.25rem',
            maxWidth: '550px',
            width: '90%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            color: '#334155',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.35rem', fontWeight: '700', color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
              Provision New Tenant Workspace
            </h3>
            <form onSubmit={handleCreateTenant}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#475569', fontSize: '0.9rem' }}>
                  Tenant Name *
                </label>
                <input
                  type="text"
                  value={newTenant.university_name}
                  onChange={(e) => setNewTenant({ ...newTenant, university_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  placeholder="e.g. University of Mumbai"
                />
                {fieldErrors.university_name && (
                  <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.35rem' }}>{fieldErrors.university_name}</div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#475569', fontSize: '0.9rem' }}>
                    Tenant Slug *
                  </label>
                  <input
                    type="text"
                    value={newTenant.slug}
                    onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    placeholder="e.g. mum"
                  />
                  {fieldErrors.slug && (
                    <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.35rem' }}>{fieldErrors.slug}</div>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#475569', fontSize: '0.9rem' }}>
                    Tenant Type *
                  </label>
                  <select
                    value={newTenant.tenant_type}
                    onChange={(e) => setNewTenant({ ...newTenant, tenant_type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    <option value="">Select a tenant type</option>
                    <option value="university">University</option>
                    <option value="college">College</option>
                    <option value="school">School</option>
                    <option value="private">Private</option>
                    <option value="other">Other</option>
                  </select>
                  {fieldErrors.tenant_type && (
                    <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.35rem' }}>{fieldErrors.tenant_type}</div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#475569', fontSize: '0.85rem' }}>
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={newTenant.contact_email}
                    onChange={(e) => setNewTenant({ ...newTenant, contact_email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    placeholder="contact@domain.edu"
                  />
                  {fieldErrors.contact_email && (
                    <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.35rem' }}>{fieldErrors.contact_email}</div>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#475569', fontSize: '0.85rem' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={newTenant.phone_number}
                    onChange={(e) => setNewTenant({ ...newTenant, phone_number: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    placeholder="e.g. +91 98765 43210"
                  />
                  {fieldErrors.phone_number && (
                    <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.35rem' }}>{fieldErrors.phone_number}</div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#475569', fontSize: '0.9rem' }}>
                  Description / Bio
                </label>
                <textarea
                  value={newTenant.description}
                  onChange={(e) => setNewTenant({ ...newTenant, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    minHeight: '75px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    outline: 'none'
                  }}
                  placeholder="Brief information about this university or college..."
                />
              </div>

              <div style={{ marginBottom: '1.75rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#475569', fontSize: '0.9rem' }}>
                  Organization Logo
                </label>
                <div style={{
                  border: '2px dashed #cbd5e1',
                  borderRadius: '0.5rem',
                  padding: '1.5rem',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  cursor: 'pointer',
                  position: 'relative'
                }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewTenant({ ...newTenant, logo: e.target.files[0] })}
                    style={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      opacity: 0, cursor: 'pointer', width: '100%'
                    }}
                  />
                  {newTenant.logo ? (
                    <div style={{ color: '#0f172a', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Icon name="CheckCircle" size={16} color="#16a34a" /> {newTenant.logo.name} selected
                    </div>
                  ) : (
                    <div>
                      <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'center' }}><Icon name="Upload" size={24} color="#94a3b8" /></div>
                      <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
                        Click or drag to upload logo
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        PNG, JPG, SVG up to 5MB
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateTenantModal(false);
                    setFieldErrors({});
                    setNewTenant({
                      slug: '', university_name: '', tenant_type: 'university',
                      contact_email: '', phone_number: '', description: '', logo: null
                    });
                  }}
                  className="btn btn-secondary"
                  disabled={submittingTenant}
                  style={{ padding: '0.6rem 1.5rem', fontWeight: '600' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submittingTenant}
                  style={{ padding: '0.6rem 1.5rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  {submittingTenant ? (
                    <>
                      <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                      Provisioning...
                    </>
                  ) : (
                    'Provision Tenant Workspace'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Error/Success Modal — rendered via a portal at document.body so it
          overlays everything (including the creation modal) regardless of the
          surrounding DOM/stacking context. */}
      {showErrorModal && createPortal(
        <div
          onClick={() => setShowErrorModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '400px', textAlign: 'center', padding: '2rem' }}
          >
            <h2 style={{
              color: errorModalContent.title === 'Success' ? 'var(--success-color)' : 'var(--danger-color)',
              marginBottom: '1rem'
            }}>
              {errorModalContent.title}
            </h2>
            <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
              {errorModalContent.message}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowErrorModal(false)}
              style={{ width: '100%' }}
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SuperAdminTenants;
